import * as React from 'react';
import { Modal } from 'rcd-cpp-ui/ui/modal/Modal';
import { Buttons, Cancel, Delete, Primary, Save } from 'rcd-cpp-ui/ui/button';
import './confirmModal.scss';

interface ConfirmModalProps {
  header: string;
  label?: string;
  cancelLabel?: string;
  disabled?: boolean;
  onCancel: () => void;
  onDelete?: () => void;
  onCreate?: () => void; // use onConfirm and change label as needed
  onUpdate?: () => void; // use onConfirm and change label as needed
  onConfirm?: () => void;
  children: any;
}
export const ConfirmModal = ({
  header,
  label,
  cancelLabel,
  disabled,
  onCancel,
  onDelete,
  onCreate,
  onUpdate,
  onConfirm,
  children
}: ConfirmModalProps) => {
  return (
    <Modal header={header} height="auto" id="confirmModal" onClose={onCancel}>
      <div style={{ padding: '10px' }}>{children}</div>
      <Buttons>
        <Cancel
          onClick={onCancel}
          label={!!cancelLabel ? cancelLabel : 'Cancel'}
        />
        {!!onDelete ? (
          <Delete
            label={!!label ? label : 'Delete'}
            onClick={onDelete}
            disabled={disabled}
          />
        ) : null}
        {!!onConfirm ? (
          <Primary label={label || 'Confirm'} onClick={onConfirm} />
        ) : null}
        {!!onCreate ? <Primary label="Create" onClick={onCreate} /> : null}
        {!!onUpdate ? <Save label="Save" onClick={onUpdate} /> : null}
      </Buttons>
    </Modal>
  );
};

const FNs = ['onConfirm', 'onDelete', 'onCancel'];
type ConfirmActions = Pick<
  Partial<ConfirmModalProps>,
  'onCancel' | 'onConfirm' | 'onDelete'
>;
type ConfirmState = ConfirmActions & {
  header?: string;
  children?: string | React.ReactNode;
};

export type ConfirmFn = (
  children: string | React.ReactNode | ConfirmState,
  actions?: ConfirmState
) => void;
export const useConfirmModal = (props: Partial<ConfirmModalProps> = {}) => {
  const [state, setState] = React.useState<ConfirmState>(null);

  const confirm: ConfirmFn = React.useCallback(
    (
      children: string | React.ReactNode | ConfirmState,
      actions?: ConfirmState
    ) => {
      const newState: ConfirmState = {};
      let isChildren = true;
      if (children && typeof children != 'string') {
        const c = children as any;
        FNs.forEach(fn => {
          if (typeof c[fn] == 'function') {
            newState[fn] = c[fn];
            isChildren = false;
          }
        });
        if (typeof c == 'function') {
          newState.onConfirm = c;
          isChildren = false;
        }
        if (c.header) newState.header = c.header;
      }
      if (isChildren) newState.children = children;
      if (actions) {
        FNs.forEach(fn => {
          if (typeof actions[fn] == 'function') {
            newState[fn] = actions[fn];
          }
        });
        if (actions.header) newState.header = actions.header;
      }
      setState(newState);
    },
    []
  );

  const Modal = () => {
    if (!state) return null;
    const { header, children, onConfirm, onCancel, onDelete } = state;
    const actions = {
      header: header || props.header,
      children: children || props.children,
      onConfirm: onConfirm || props.onConfirm,
      onDelete: onDelete || props.onDelete,
      onCancel: onCancel || props.onCancel || (() => {})
    };
    if (!actions.children || (!actions.onConfirm && !actions.onDelete))
      return null;
    FNs.forEach(fn => {
      if (actions[fn]) {
        const action = actions[fn];
        actions[fn] = () => {
          action();
          setState(null);
        };
      }
    });
    return <ConfirmModal header="Confirm" {...props} {...actions} />;
  };

  return { confirm, ConfirmModal: Modal };
};

export const useImpliedDataConfirmModal = () =>
  useConfirmModal({
    header: 'Accept Implied Value',
    children: (
      <div>
        This will replace the existing value in the field. <br />
        Are you sure you want to proceed?
      </div>
    )
  });
